/**
 * Lazy Loading Utility
 *
 * Provides optimized lazy loading for React components with preloading capabilities
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import { recoverFromStaleChunk } from './staleChunkRecovery';

/** Lazy-loaded component plus preload; `P` is the props object type (same as `ComponentType<P>`). */
export interface LazyComponent<P extends object = object> {
  Component: LazyExoticComponent<ComponentType<P>>;
  preload: () => Promise<void>;
}

interface LazyComponentOptions {
  preload?: boolean;
  delay?: number;
}

const componentCache = new Map<string, Promise<{ default: ComponentType<any> }>>();

/**
 * Create a lazy component with optional preloading.
 *
 * - **`P`**: Props object type for the returned `Component` (use `createLazyComponent<MyProps>(...)` when you need strict props).
 * - **`importFn`**: Uses `ComponentType<any>` on the callback so patterns like
 *   `import('./X').then((m) => ({ default: m.NamedExport }))` (union/weak inference) still typecheck.
 * - **Cache**: Stores `any` at the module boundary; the returned lazy component is asserted to `ComponentType<P>`.
 *
 * A rejected import is evicted from the cache so the next render retries the
 * network instead of replaying the stored rejection forever, and a chunk that is
 * missing because of a deploy triggers a single recovery reload.
 */
export function createLazyComponent<P extends object = object>(
  importFn: () => Promise<{ default: ComponentType<any> }>,
  options: LazyComponentOptions = {},
): LazyComponent<P> {
  const { preload = false, delay = 0 } = options;

  const componentKey = importFn.toString();

  const resolveModule = (): Promise<{ default: ComponentType<any> }> => importFn();

  const loadModule = (): Promise<{ default: ComponentType<any> }> => {
    const cached = componentCache.get(componentKey);
    if (cached) {
      return cached;
    }

    const importPromise =
      delay > 0
        ? new Promise<{ default: ComponentType<any> }>((resolve, reject) => {
            setTimeout(() => {
              resolveModule().then(resolve, reject);
            }, delay);
          })
        : resolveModule();

    // Keeping a rejected promise in the cache makes one transient network blip
    // permanent for the lifetime of the tab: every retry replays the stored
    // rejection and the route can never render again.
    const trackedPromise = importPromise.catch((error: unknown) => {
      componentCache.delete(componentKey);
      throw error;
    });

    componentCache.set(componentKey, trackedPromise);
    return trackedPromise;
  };

  const Component = lazy(() =>
    loadModule().catch((error: unknown) => {
      recoverFromStaleChunk(error);
      throw error;
    }),
  ) as LazyExoticComponent<ComponentType<P>>;

  // Preloading is speculative: the user has not asked for this route yet, so a
  // failure must stay silent instead of becoming an unhandled rejection. The
  // render path retries and reports if the route is actually visited.
  const preloadFn = async (): Promise<void> => {
    try {
      await loadModule();
    } catch {
      return;
    }
  };

  if (preload) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => void preloadFn());
    } else {
      setTimeout(() => void preloadFn(), 100);
    }
  }

  return {
    Component,
    preload: preloadFn,
  };
}

/**
 * Preload multiple components
 */
export function preloadComponents(components: Array<() => Promise<void>>): Promise<void[]> {
  return Promise.all(components.map((preload) => preload()));
}

/**
 * Create a route-based preloader
 */
export class RoutePreloader {
  private readonly preloadMap = new Map<string, () => Promise<void>>();
  private readonly preloadedRoutes = new Set<string>();

  register(route: string, preloadFn: () => Promise<void>): void {
    this.preloadMap.set(route, preloadFn);
  }

  async preload(route: string): Promise<void> {
    if (this.preloadedRoutes.has(route)) {
      return;
    }

    const preloadFn = this.preloadMap.get(route);
    if (preloadFn) {
      await preloadFn();
      this.preloadedRoutes.add(route);
    }
  }

  preloadOnHover(route: string): void {
    setTimeout(() => {
      void this.preload(route);
    }, 100);
  }

  preloadOnVisible(route: string, element: Element): void {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              void this.preload(route);
              observer.unobserve(element);
            }
          });
        },
        { threshold: 0.1 },
      );
      observer.observe(element);
    }
  }
}

export const routePreloader = new RoutePreloader();
